import { useEffect, useMemo, useRef, useState } from "react";
import { FileValidationType, checkFileValidation } from "../../validators/file";
import { addBackEndDomain } from "../../utils/backend-domain";
import Button from "./button";

type UploadImageProps = {
  id?: string;
  url?: string|null;
  fileValidation?: FileValidationType;
  onChange: React.Dispatch<React.SetStateAction<File|null>>;
}

const UploadImage = ({fileValidation, id, url, onChange}: UploadImageProps) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setFile] = useState<File|null>(null);
  const previewImage = useMemo(() => !selectedFile && url ? url : null, [selectedFile, url]);
  const displayFileState = useMemo(() => url && selectedFile || !url , [selectedFile, url]);
  const [validationErrors, setValidationErrors] = useState<string[]|null>(null);
  
  // Update file input accept attribute to include webp
  const acceptedTypes = useMemo(() => {
    if (!fileValidation?.types) return undefined;
    return fileValidation.types
      .map(type => `.${type}`)
      .concat(['.webp']) // Add .webp to accepted types
      .join(',');
  }, [fileValidation?.types]);

  /** On file selected */
  useEffect(() => {
    onChange(selectedFile)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile])

  useEffect(()=>{
    if (url) {
      resetFileInput();
    }
  },[url]);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      const file = fileList[0];
      if (file && fileValidation) {
        // Add webp to the validation types if not already included
        const validationWithWebp = {
          ...fileValidation,
          types: fileValidation.types ? 
            Array.from(new Set([...fileValidation.types, 'webp'])) : 
            ['webp']
        };
        
        const {valid, errors} = checkFileValidation(file, validationWithWebp);
        if (valid) {
          setFile(file);
        } else if (errors && errors.length > 0) {
          setValidationErrors(errors);
        }
      } else {
        setFile(file);
      }
    } else {
      setFile(null);
    }
  };

  const resetFileInput = () => {
    setFile(null);
    if (inputFileRef.current) {
      inputFileRef.current.value = '';
    }
  };

  return (
    <>
    <input 
      ref={inputFileRef} 
      type="file" 
      id={id}
      accept={acceptedTypes}
      className="hidden"
      onChange={handleFileChange} 
    />
    <div className="w-full relative">
      <div className="p-3.5 border border-dashed border-izygray rounded-lg text-sm text-izygray">
        {
          fileValidation &&
          <div className="mb-3">
            {
              fileValidation.types &&
              <p>Formats acceptés : {[...fileValidation.types, 'webp'].map(format => `.${format}`).join(' - ')}</p>
            }
            {
              fileValidation.maxSize &&
              <p>Max : {fileValidation.maxSize} Mo</p>
            }
          </div>
        }
        <div className="flex items-center gap-[30px]">
          <Button width="fit" theme="black" className="px-5 bg-[#A2B0BF]"
            onClick={() => {
              inputFileRef.current?.click() 
              setValidationErrors(null)
            }}>Charger une image
          </Button>
          {
            displayFileState &&
            <p className={`m-0 ${selectedFile ? 'text-shadow' : ''}`}>
              {selectedFile ? selectedFile.name : "Pas de fichier chargé."}
              {selectedFile && <span role="button" className="mx-3 inline-block text-red-500 font-bold"
                onClick={() => resetFileInput()}>x</span>}
            </p>
          }
        </div>
        {
          validationErrors && validationErrors.map((error, index) => (
            <span key={index} className="my-3 text-xs leading-tight block text-red-600">{error}</span>
          ))
        }
        {
          previewImage &&
          <div className="mt-5">
            <img src={addBackEndDomain(previewImage)}
              className="m-auto h-[100px] md:h-[200px] aspect-square object-cover object-center"
            />
          </div>
        }
      </div>
    </div>
    </>
  )
}

export default UploadImage;